import React from 'react';
import { Alert, AlertTitle, Button, Box, Collapse, Typography } from '@mui/material';
import { Refresh as RetryIcon } from '@mui/icons-material';

interface ErrorAlertProps {
  error: string | null;
  helpText?: string | null;
  onClear?: () => void;
  onRetry?: () => void;
  title?: string;
  severity?: 'error' | 'warning' | 'info';
  showRetry?: boolean;
  sx?: any;
}

/**
 * Simple, direct error display component
 * Much simpler than the event bus approach
 */
export const ErrorAlert: React.FC<ErrorAlertProps> = ({
  error,
  onClear,
  onRetry,
  title,
  severity = 'error',
  showRetry = true,
  helpText,
  sx,
}) => {
  if (!error) return null;

  return (
    <Collapse in={!!error}>
      <Alert
        severity={severity}
        onClose={onClear}
        sx={{
          mb: 2,
          ...sx,
        }}
        action={
          showRetry && onRetry && (
            <Button
              size="small"
              startIcon={<RetryIcon />}
              onClick={onRetry}
              variant="outlined"
              color={severity}
            >
              Retry
            </Button>
          )
        }
      >
        {title && <AlertTitle>{title}</AlertTitle>}
        <Box>
          <Typography variant="body2" sx={{ mb: helpText ? 1 : 0 }}>
            {error}
          </Typography>
          {helpText && (
            <Typography 
              variant="body2" 
              sx={{ 
                opacity: 0.8,
                fontStyle: 'italic',
                fontSize: '0.875rem'
              }}
            >
              {helpText}
            </Typography>
          )}
        </Box>
      </Alert>
    </Collapse>
  );
};

/**
 * Inline error display for form fields
 */
export const FieldError: React.FC<{
  error?: string;
  touched?: boolean;
}> = ({ error, touched }) => {
  if (!error || !touched) return null;

  return (
    <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
      {error}
    </Alert>
  );
};

export default ErrorAlert; 